import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/hooks/useAuth';

export default function LoginPage() {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [formData, setFormData] = useState({ email: '', password: '' });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await api.post('/api/auth/login', {
        email: formData.email,
        password: formData.password
      });

      if (response.data.success) {
        const userData = {
          id: response.data.data.userId || response.data.data.id,
          username: response.data.data.username,
          email: response.data.data.email,
          fullName: response.data.data.fullName,
          phone: response.data.data.phone,
          address: response.data.data.address,
          city: response.data.data.city,
          governorate: response.data.data.governorate,
          role: response.data.data.role,
          roleName: response.data.data.roleName,
          permissions: response.data.data.permissions || [],
          delegateId: response.data.data.delegateId,
        };
        login(userData, response.data.data.token);
        
        // توجيه ذكي بناءً على دور المستخدم
        const role = response.data.data.role;
        if (role === 'merchant' || role === 'تاجر') {
          navigate('/merchant/dashboard');
        } else {
          navigate('/dashboard');
        }
      } else {
        setError(response.data.message || 'فشل تسجيل الدخول');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'حدث خطأ أثناء تسجيل الدخول');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-white" dir="rtl">
      <div className="max-w-md w-full bg-slate-900/80 backdrop-blur-md rounded-2xl shadow-2xl p-8 mx-4 border-t-4 border-orange-400">
        {/* Logo Section */}
        <div className="text-center mb-8">
          <div className="mb-6 flex justify-center">
              <img src="/icon-512-from-server.png" alt="Target Express Logo" className="h-36 w-auto object-contain drop-shadow-lg" />
            <div id="logo-fallback" className="hidden w-36 h-36 bg-gradient-to-br from-orange-500 to-orange-600 rounded-2xl items-center justify-center shadow-xl">
              <span className="text-white text-5xl font-bold">TE</span>
            </div>
          </div>
          <h1 className="text-3xl font-bold text-white">
            Target Express
          </h1>
          <p className="text-gray-300 mt-2">Shipping and Logistics Solutions</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm flex items-center gap-2">
              <svg className="w-5 h-5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              {error}
            </div>
          )}

          <div>
            {/* تم تغيير اللون إلى الأبيض ليظهر بوضوح على الخلفية الداكنة */}
            <label className="block text-sm font-semibold text-white mb-2">
              البريد الإلكتروني
            </label>
            <input
              type="email"
              required
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-all outline-none text-gray-800 bg-white"
              placeholder="example@email.com" 
            />
          </div>

          <div>
            {/* تم تغيير اللون إلى الأبيض ليظهر بوضوح على الخلفية الداكنة */}
            <label className="block text-sm font-semibold text-white mb-2">
              كلمة المرور
            </label>
            <input
              type="password"
              required
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="w-full px-4 py-3 border-2 border-gray-600 rounded-lg focus:ring-2 focus:ring-orange-400 focus:border-orange-400 transition-all outline-none text-gray-800 bg-white"
              placeholder="••••••••"
            />
            <div className="flex justify-end mt-2">
              <Link to="/forgot-password" className="text-sm text-orange-400 hover:text-orange-300 transition">
                نسيت كلمة المرور؟
              </Link>
            </div>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-bold py-3.5 rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed transform hover:-translate-y-0.5"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                جاري تسجيل الدخول...
              </span>
            ) : (
              'دخول'
            )}
          </button>
        </form>

        {/* Register Link */}
        <div className="mt-5 text-center">
          <p className="text-sm text-gray-300">
            ليس لديك حساب؟{" "}
            <Link
              to="/register"
              className="text-orange-400 hover:text-orange-300 font-semibold transition"
            >
              سجّل حسابًا
            </Link>
          </p>
        </div>

        {/* Footer */}
        <div className="mt-6 pt-6 border-t border-gray-600 text-center">
          <p className="text-sm text-gray-400">© 2026 Target Express. جميع الحقوق محفوظة</p>
          <p className="text-xs text-gray-500 mt-1">Shipping and Logistics Solutions</p>
        </div>
      </div>
    </div>
  );
}